import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  MapPin,
  Phone,
  Mail,
  StickyNote,
  Pencil,
  Map as MapIcon,
  Tag as TagIcon,
  Camera,
  Play,
  User2,
  Link as LinkIcon,
  ExternalLink,
  X,
  CalendarDays,
} from "lucide-react";
import { driveThumbnailUrl } from "@/lib/imageUtil";
import MediaViewer from "@/components/MediaViewer";
import BookingsSection from "@/components/BookingsSection";

function looksLikeUrl(v) {
  if (!v || typeof v !== "string") return false;
  const s = v.trim();
  if (/^https?:\/\//i.test(s)) return true;
  if (/^www\./i.test(s)) return true;
  // bare domain like "example.com/path"
  if (/^[\w-]+(\.[\w-]+){1,}(\/.*)?$/.test(s)) return true;
  return false;
}

function looksLikeEmail(v) {
  return typeof v === "string" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
}

function asUrl(v) {
  const s = String(v).trim();
  if (/^https?:\/\//i.test(s)) return s;
  return `https://${s.replace(/^\/+/, "")}`;
}

/** A centered detail card opened by tapping a marker / contact in the list. */
export default function ContactProfile({
  open,
  contact,
  tagById,
  onOpenChange,
  onEdit,
  onShowOnMap,
}) {
  const [viewerStart, setViewerStart] = useState(null);
  // Reset the gallery whenever the profile is closed or switched to another
  // contact, otherwise it can stick open across opens.
  useEffect(() => {
    if (!open || !contact) setViewerStart(null);
  }, [open, contact?.id]);
  if (!contact) return null;
  const tags = (contact.tag_ids || []).map((id) => tagById.get(id)).filter(Boolean);
  const media = contact.media || [];
  const initials = (contact.name || "")
    .split(/\s+/)
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl bg-[#111] border border-white/10 text-white p-0 max-h-[92vh] overflow-hidden flex flex-col [&>button.absolute]:hidden"
        data-testid="contact-profile"
        onPointerDownOutside={(e) => {
          // Don't dismiss the profile when the click originated inside the
          // full-screen MediaViewer (which is portaled to body).
          const target = e.detail?.originalEvent?.target;
          if (target instanceof Element && target.closest("[data-testid='media-viewer']")) {
            e.preventDefault();
          }
        }}
        onInteractOutside={(e) => {
          const target = e.detail?.originalEvent?.target;
          if (target instanceof Element && target.closest("[data-testid='media-viewer']")) {
            e.preventDefault();
          }
        }}
        onEscapeKeyDown={(e) => {
          // If the gallery viewer is open, let it handle Escape itself.
          if (document.querySelector("[data-testid='media-viewer']")) {
            e.preventDefault();
          }
        }}
      >
        {/* Top-right close pill — wrapped in a div so the dialog's
            [&>button.absolute]:hidden selector doesn't also hide it. */}
        <div className="absolute top-3 right-3 z-10">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="h-10 w-10 rounded-full bg-[#ff5a5a]/15 border border-[#ff5a5a]/40 text-[#ff7a7a] hover:bg-[#ff5a5a]/25 hover:text-[#ff9a9a] flex items-center justify-center transition-colors"
            aria-label="Close"
            data-testid="profile-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <DialogHeader className="px-6 pt-6 pb-4 pr-16 border-b border-white/10">
          <div className="flex items-start gap-4">
            {contact.avatar ? (
              <img
                src={contact.avatar}
                alt=""
                className="h-20 w-20 rounded-full object-cover border-2 border-[#00e5ff]/40 shrink-0"
                draggable={false}
              />
            ) : (
              <div className="h-20 w-20 rounded-full bg-white/5 border-2 border-[#00e5ff]/40 flex items-center justify-center font-display text-2xl tracking-wider text-[#00e5ff] shrink-0">
                {initials || <User2 className="h-8 w-8" />}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <DialogTitle className="font-display uppercase text-2xl tracking-wider text-white">
                {contact.name}
              </DialogTitle>
              <DialogDescription className="font-mono text-xs text-white/50 mt-1 flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5 text-[#f5d300]" />
                {[contact.town, contact.region].filter(Boolean).join(" · ") ||
                  contact.location_name ||
                  `${contact.lat.toFixed(4)}, ${contact.lng.toFixed(4)}`}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="px-6 py-5 overflow-y-auto thin-scroll space-y-5 flex-1">
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {tags.map((t) => (
                <span
                  key={t.id}
                  className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest"
                  style={{
                    background: `${t.color}33`,
                    border: `1px solid ${t.color}AA`,
                    color: t.color,
                  }}
                >
                  <TagIcon className="h-3 w-3" />
                  {t.name}
                </span>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 font-mono text-sm">
            {(contact.street || contact.postcode || contact.country) && (
              <div className="sm:col-span-2 flex items-start gap-2 text-white/75">
                <MapPin className="h-4 w-4 text-[#f5d300] mt-0.5" />
                {[contact.street, contact.postcode, contact.country]
                  .filter(Boolean)
                  .join(", ")}
              </div>
            )}
            {contact.phone && (
              <a
                href={`tel:${contact.phone}`}
                className="flex items-center gap-2 text-white/80 hover:text-[#00e5ff]"
              >
                <Phone className="h-4 w-4 text-[#00e5ff]" /> {contact.phone}
              </a>
            )}
            {contact.email && (
              <a
                href={`mailto:${contact.email}`}
                className="flex items-center gap-2 text-white/80 hover:text-[#00e5ff] truncate"
              >
                <Mail className="h-4 w-4 text-[#00e5ff]" />
                <span className="truncate">{contact.email}</span>
              </a>
            )}
          </div>

          {contact.notes && (
            <div className="flex items-start gap-2 text-white/75 font-mono text-sm leading-relaxed">
              <StickyNote className="h-4 w-4 text-[#00e5ff] mt-0.5" />
              <p className="whitespace-pre-wrap">{contact.notes}</p>
            </div>
          )}

          {(contact.custom_fields || []).length > 0 && (
            <div className="space-y-2 pt-3 border-t border-white/10">
              <div className="flex items-center gap-2">
                <LinkIcon className="h-3.5 w-3.5 text-[#00e5ff]" />
                <span className="text-xs uppercase tracking-widest text-white/60 font-mono">
                  Links & Details
                </span>
              </div>
              <ul
                className="divide-y divide-white/5 rounded-lg border border-white/10 bg-white/[0.02]"
                data-testid="contact-profile-custom-fields"
              >
                {(contact.custom_fields || []).map((cf, i) => {
                  const k = cf.key || "—";
                  const v = cf.value || "";
                  const isLink = looksLikeUrl(v);
                  const isEmail = !isLink && looksLikeEmail(v);
                  const linkHref = isLink ? asUrl(v) : isEmail ? `mailto:${v}` : null;
                  return (
                    <li
                      key={`${k}-${i}`}
                      className="px-3 py-2 flex items-center gap-3"
                      data-testid={`contact-profile-field-${i}`}
                    >
                      {linkHref ? (
                        <a
                          href={linkHref}
                          target={isLink ? "_blank" : undefined}
                          rel={isLink ? "noopener noreferrer" : undefined}
                          className="flex-1 min-w-0 inline-flex items-center gap-1.5 text-[#00e5ff] hover:text-[#7af3ff] font-mono text-sm uppercase tracking-widest hover:underline underline-offset-2"
                        >
                          <span className="truncate">{k}</span>
                          <ExternalLink className="h-3 w-3 shrink-0 opacity-80" />
                        </a>
                      ) : (
                        <>
                          <span className="text-[10px] uppercase tracking-widest font-mono text-white/45 w-28 shrink-0">
                            {k}
                          </span>
                          <span className="font-mono text-sm flex-1 min-w-0 break-words text-white/85">
                            {v}
                          </span>
                        </>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {media.length > 0 && (
            <div className="space-y-2 pt-2 border-t border-white/10">
              <div className="flex items-center gap-2">
                <Camera className="h-3.5 w-3.5 text-[#00e5ff]" />
                <span className="text-xs uppercase tracking-widest text-white/60 font-mono">
                  Gallery · {media.length}
                </span>
              </div>
              <div
                className="grid grid-cols-3 sm:grid-cols-4 gap-2"
                data-testid="contact-profile-gallery"
              >
                {media.map((m, i) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setViewerStart(i)}
                    className="aspect-square rounded-lg overflow-hidden border border-white/10 bg-black relative group"
                    data-testid={`profile-media-${m.id}`}
                  >
                    {m.type === "image" ? (
                      <img
                        src={m.data || m.url}
                        alt=""
                        className="w-full h-full object-cover"
                        draggable={false}
                      />
                    ) : (
                      <>
                        <img
                          src={driveThumbnailUrl(m.url) || ""}
                          alt=""
                          className="w-full h-full object-cover opacity-70"
                          draggable={false}
                          onError={(e) => (e.currentTarget.style.display = "none")}
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                          <Play className="h-7 w-7 text-white" fill="white" />
                        </div>
                      </>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Bookings & journal */}
          <div className="pt-2 border-t border-white/10">
            <BookingsSection
              contactId={contact.id}
              contactName={contact.name}
              kind={contact.kind || "contact"}
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-white/10 flex items-center justify-end gap-2">
          {onShowOnMap && (
            <Button
              onClick={onShowOnMap}
              variant="ghost"
              className="btn-ghost h-11 px-3"
              data-testid="profile-show-on-map"
            >
              <MapIcon className="h-4 w-4 mr-1.5" /> On map
            </Button>
          )}
          <Button
            onClick={onEdit}
            className="btn-neon h-11 px-4"
            data-testid="profile-edit"
          >
            <Pencil className="h-4 w-4 mr-1.5" /> Edit
          </Button>
        </div>

        {viewerStart != null && (
          <MediaViewer
            items={media}
            startIndex={viewerStart}
            onClose={() => setViewerStart(null)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
